/**
 * Colour schemes: complete, curated palettes for the whole console — canvas,
 * glass panels, text, lines, the primary (ink) button, table headers and the
 * default accent — each in a light and a dark variant. They are applied as
 * `--s-*` (light) and `--sd-*` (dark) CSS variables that the design tokens in
 * index.css read, so a scheme applies before first paint like the accent does,
 * and the Background choice (warm, cool, plain) still layers on top.
 *
 * Text pairs meet WCAG AA: body text (fg) on the canvas and on panels is 12:1
 * or more, secondary text (muted) 4.5:1 or more; accents are dark enough for
 * white labels in light mode and light enough on the dark canvas.
 */

export type SchemeKey = 'white' | 'ocean' | 'red'

export const SCHEME_TOKENS = [
  'canvas',
  'canvas-ambient-1',
  'canvas-ambient-2',
  'canvas-ambient-3',
  'glass',
  'glass-strong',
  'glass-solid',
  'menu',
  'glass-edge',
  'fg',
  'fg-2',
  'muted',
  'subtle',
  'line',
  'line-strong',
  'hover',
  'press',
  'field-border',
  'ink',
  'ink-hover',
  'ink-fg',
  'table-head',
  'table-head-fg',
] as const

export type SchemeToken = (typeof SCHEME_TOKENS)[number]
type Palette = Record<SchemeToken, string>

/** `accentDark` pins the accent on the dark theme; without it the accent is lightened. */
export type ColorScheme = { accent: string; accentDark?: string; light: Palette; dark: Palette }

export const COLOR_SCHEMES: Record<SchemeKey, ColorScheme> = {
  // Clean white and neutral greys with black buttons: the plainest look.
  white: {
    accent: '#16191D',
    accentDark: '#E4E6E9',
    light: {
      canvas: '#f4f5f6',
      'canvas-ambient-1': 'rgba(150, 155, 165, 0.14)',
      'canvas-ambient-2': 'rgba(200, 200, 205, 0.14)',
      'canvas-ambient-3': 'rgba(170, 175, 185, 0.12)',
      glass: 'rgba(255, 255, 255, 0.66)',
      'glass-strong': 'rgba(255, 255, 255, 0.86)',
      'glass-solid': '#ffffff',
      menu: 'rgba(255, 255, 255, 0.98)',
      'glass-edge': 'rgba(20, 22, 26, 0.07)',
      fg: '#15181c',
      'fg-2': '#3b4047',
      muted: '#5f656e',
      subtle: '#979ca4',
      line: 'rgba(20, 22, 26, 0.08)',
      'line-strong': 'rgba(20, 22, 26, 0.14)',
      hover: 'rgba(20, 22, 26, 0.045)',
      press: 'rgba(20, 22, 26, 0.08)',
      'field-border': 'rgba(20, 22, 26, 0.13)',
      ink: '#16191D',
      'ink-hover': '#2C3137',
      'ink-fg': '#ffffff',
      'table-head': '#f5f6f7',
      'table-head-fg': '#4d535b',
    },
    dark: {
      canvas: '#0c0c0d',
      'canvas-ambient-1': 'rgba(120, 120, 130, 0.12)',
      'canvas-ambient-2': 'rgba(150, 150, 160, 0.08)',
      'canvas-ambient-3': 'rgba(90, 95, 105, 0.12)',
      glass: 'rgba(28, 29, 31, 0.55)',
      'glass-strong': 'rgba(33, 34, 37, 0.8)',
      'glass-solid': '#18191b',
      menu: 'rgba(26, 27, 29, 0.97)',
      'glass-edge': 'rgba(0, 0, 0, 0.45)',
      fg: '#ededee',
      'fg-2': '#c9cacd',
      muted: '#97999f',
      subtle: '#6c6e74',
      line: 'rgba(255, 255, 255, 0.07)',
      'line-strong': 'rgba(255, 255, 255, 0.12)',
      hover: 'rgba(255, 255, 255, 0.05)',
      press: 'rgba(255, 255, 255, 0.09)',
      'field-border': 'rgba(255, 255, 255, 0.1)',
      ink: '#f0f0f1',
      'ink-hover': '#ffffff',
      'ink-fg': '#121315',
      'table-head': '#202124',
      'table-head-fg': '#b1b3b8',
    },
  },
  // The brand blue (#0158C5, the ALFI logo's blue tile) on every button, line
  // and pressed state where the other schemes use near-black ink. The default.
  ocean: {
    accent: '#0158C5',
    light: {
      canvas: '#edf1f6',
      'canvas-ambient-1': 'rgba(1, 88, 197, 0.18)',
      'canvas-ambient-2': 'rgba(96, 150, 210, 0.14)',
      'canvas-ambient-3': 'rgba(120, 140, 190, 0.14)',
      glass: 'rgba(255, 255, 255, 0.58)',
      'glass-strong': 'rgba(255, 255, 255, 0.8)',
      'glass-solid': '#fbfcfe',
      menu: 'rgba(251, 252, 254, 0.97)',
      'glass-edge': 'rgba(1, 88, 197, 0.1)',
      fg: '#0f1b2d',
      'fg-2': '#334560',
      muted: '#586a84',
      subtle: '#8e9bb0',
      line: 'rgba(1, 88, 197, 0.12)',
      'line-strong': 'rgba(1, 88, 197, 0.2)',
      hover: 'rgba(1, 88, 197, 0.06)',
      press: 'rgba(1, 88, 197, 0.11)',
      'field-border': 'rgba(1, 88, 197, 0.2)',
      ink: '#0158C5',
      'ink-hover': '#014AA8',
      'ink-fg': '#ffffff',
      'table-head': '#e8f0fb',
      'table-head-fg': '#2b4a78',
    },
    dark: {
      canvas: '#090d14',
      'canvas-ambient-1': 'rgba(50, 100, 190, 0.22)',
      'canvas-ambient-2': 'rgba(80, 120, 200, 0.12)',
      'canvas-ambient-3': 'rgba(60, 90, 150, 0.14)',
      glass: 'rgba(22, 30, 44, 0.55)',
      'glass-strong': 'rgba(26, 35, 52, 0.8)',
      'glass-solid': '#131b28',
      menu: 'rgba(20, 28, 41, 0.97)',
      'glass-edge': 'rgba(0, 0, 0, 0.45)',
      fg: '#e7edf6',
      'fg-2': '#c1cce0',
      muted: '#8b9ab3',
      subtle: '#637189',
      line: 'rgba(120, 170, 255, 0.1)',
      'line-strong': 'rgba(120, 170, 255, 0.17)',
      hover: 'rgba(120, 170, 255, 0.07)',
      press: 'rgba(120, 170, 255, 0.12)',
      'field-border': 'rgba(120, 170, 255, 0.16)',
      ink: '#1E6AD4',
      'ink-hover': '#2F7BE0',
      'ink-fg': '#ffffff',
      'table-head': '#172133',
      'table-head-fg': '#a9b7cf',
    },
  },
  // The brand red (the ALFI logo's red tile, #F10D19) on buttons, lines and
  // pressed states. Buttons use #DC0C17, the nearest shade that keeps white
  // labels at AA (5.1:1; #F10D19 itself gives 4.4:1).
  red: {
    accent: '#DC0C17',
    light: {
      canvas: '#f6f1f1',
      'canvas-ambient-1': 'rgba(241, 13, 25, 0.12)',
      'canvas-ambient-2': 'rgba(230, 120, 110, 0.12)',
      'canvas-ambient-3': 'rgba(200, 110, 120, 0.1)',
      glass: 'rgba(255, 255, 255, 0.6)',
      'glass-strong': 'rgba(255, 255, 255, 0.82)',
      'glass-solid': '#fefcfc',
      menu: 'rgba(254, 252, 252, 0.97)',
      'glass-edge': 'rgba(220, 12, 23, 0.09)',
      fg: '#1f1414',
      'fg-2': '#4a3434',
      muted: '#735a5a',
      subtle: '#aa9494',
      line: 'rgba(220, 12, 23, 0.11)',
      'line-strong': 'rgba(220, 12, 23, 0.19)',
      hover: 'rgba(220, 12, 23, 0.055)',
      press: 'rgba(220, 12, 23, 0.1)',
      'field-border': 'rgba(220, 12, 23, 0.18)',
      ink: '#DC0C17',
      'ink-hover': '#B80913',
      'ink-fg': '#ffffff',
      'table-head': '#fbeeee',
      'table-head-fg': '#6b3b3d',
    },
    dark: {
      canvas: '#110a0a',
      'canvas-ambient-1': 'rgba(241, 13, 25, 0.16)',
      'canvas-ambient-2': 'rgba(200, 90, 80, 0.1)',
      'canvas-ambient-3': 'rgba(160, 70, 80, 0.12)',
      glass: 'rgba(40, 24, 25, 0.55)',
      'glass-strong': 'rgba(46, 28, 29, 0.8)',
      'glass-solid': '#1f1516',
      menu: 'rgba(33, 22, 23, 0.97)',
      'glass-edge': 'rgba(0, 0, 0, 0.45)',
      fg: '#f5e9e9',
      'fg-2': '#dcc6c6',
      muted: '#ab9393',
      subtle: '#7d6666',
      line: 'rgba(255, 140, 140, 0.1)',
      'line-strong': 'rgba(255, 140, 140, 0.17)',
      hover: 'rgba(255, 140, 140, 0.07)',
      press: 'rgba(255, 140, 140, 0.12)',
      'field-border': 'rgba(255, 140, 140, 0.16)',
      ink: '#DC0C17',
      'ink-hover': '#EE2A33',
      'ink-fg': '#ffffff',
      'table-head': '#2b1c1d',
      'table-head-fg': '#c9abab',
    },
  },
}

export const SCHEME_KEYS = Object.keys(COLOR_SCHEMES) as SchemeKey[]

/** The CSS variables a scheme sets on <html>: `--s-*` for light, `--sd-*` for dark. */
export function schemeVars(key: SchemeKey): Record<string, string> {
  const scheme = COLOR_SCHEMES[key] ?? COLOR_SCHEMES.ocean
  const vars: Record<string, string> = {}
  for (const token of SCHEME_TOKENS) {
    vars[`--s-${token}`] = scheme.light[token]
    vars[`--sd-${token}`] = scheme.dark[token]
  }
  return vars
}
